'use client';

import Link from 'next/link';
import { ContactModal } from '@/components/contact-modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Award, Users, Target, CheckCircle } from 'lucide-react';
import { useState } from 'react';

const milestones = [
  { year: '2021', event: '公司成立，专注科技服务领域' },
  { year: '2022', event: '服务企业突破100家，建立标准化服务体系' },
  { year: '2023', event: '拓展至科技成果评价和贸易增量服务' },
  { year: '2024', event: '累计服务企业500+，成为行业领先服务商' },
];

const values = [
  { title: '专业', icon: Award, desc: '深耕科技服务领域，拥有资深专家团队' },
  { title: '高效', icon: Target, desc: '标准化流程，专人对接，确保项目高效推进' },
  { title: '可靠', icon: CheckCircle, desc: '成果导向，确保每项服务带来实际价值' },
  { title: '共赢', icon: Users, desc: '与企业共同成长，建立长期合作伙伴关系' },
];

const stats = [
  { label: '服务企业', value: '500+' },
  { label: '标准参编', value: '100+' },
  { label: '成果评价', value: '200+' },
  { label: '贸易增长', value: '35%' },
];

export default function AboutPage() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Breadcrumb */}
      <section className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <nav className="text-sm text-gray-600">
            <Link href="/" className="hover:text-blue-600">首页</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">关于我们</span>
          </nav>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="bg-blue-500 mb-4">专业科技服务平台</Badge>
            <h1 className="text-4xl font-bold tracking-tight lg:text-5xl mb-6">
              关于中科企服
            </h1>
            <p className="text-lg text-blue-100 leading-relaxed">
              中科企服（湖北）科技有限公司成立于2021年，是一家专注于为企业提供标准参编、
              科技成果评价、贸易增量等专业服务的科技服务公司。我们致力于帮助企业提升技术实力与市场竞争力，
              助力企业创新发展。
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-12 border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-blue-600">{stat.value}</div>
                <div className="mt-2 text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">公司简介</h2>
            <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
              <p>
                中科企服（湖北）科技有限公司是一家专业的科技服务平台，总部位于武汉经济技术开发区。
                公司专注于为中小企业提供标准参编、科技成果评价、贸易增量等一站式专业服务。
              </p>
              <p>
                我们拥有资深行业专家和技术顾问团队，具备丰富的项目经验和专业知识储备。
                通过标准化的服务流程和专人对接机制，确保项目高效推进，缩短交付周期。
              </p>
              <p>
                公司秉承"专业、高效、可靠、共赢"的服务理念，以结果为导向，确保每项服务都能为企业带来实质性价值。
                截至目前，已累计服务企业超过500家，在行业内树立了良好的口碑和信誉。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Scope */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">主营业务</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <span className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center mr-3 text-xl">📝</span>
                  标准参编服务
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  协助企业参与国家标准、行业标准、团体标准的制定工作，提升企业在行业内的话语权和影响力。
                  服务内容包括标准立项、起草、审核、发布全流程。
                </p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <span className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center mr-3 text-xl">🏆</span>
                  科技成果评价
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  为企业的技术创新成果提供权威认证和价值评估，包括技术水平评价、创新价值评估、成果认证服务，
                  助力企业技术成果转化和产业化应用。
                </p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <span className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center mr-3 text-xl">📈</span>
                  贸易增量服务
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  通过数据分析与市场策略，帮助企业实现贸易额的稳步增长。提供市场分析、增长策略制定、
                  数据驱动决策等服务，拓展市场空间。
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">核心价值</h2>
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {values.map((value) => (
              <div key={value.title} className="text-center p-6">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development History */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">发展历程</h2>
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-blue-200"></div>
              {milestones.map((milestone, index) => (
                <div key={index} className="relative pl-12 pb-8 last:pb-0">
                  <div className="absolute left-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{milestone.year}</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">联系我们</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            欢迎致电咨询或莅临公司洽谈合作
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto text-left">
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="font-semibold mb-2">联系电话</h3>
              <p className="text-blue-100 mb-2">李老师</p>
              <a href="tel:4000757209" className="text-white font-bold text-lg hover:underline">
                187-0278-9722
              </a>
            </div>
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="font-semibold mb-2">电子邮箱</h3>
              <a href="mailto:781260356@qq.com" className="text-white hover:underline">
                781260356@qq.com
              </a>
            </div>
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="font-semibold mb-2">公司地址</h3>
              <p className="text-blue-100 text-sm">
                武汉经济技术开发区车城东路10号创思汇科技大厦16层1601-43号室
              </p>
            </div>
          </div>
        </div>
      </section>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
}